import React, { useState } from 'react';
import {
  List,
  Typography,
  Input,
  Button,
  Space,
  Popconfirm,
  Empty,
  Spin,
  Tag,
  Card,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  UserOutlined,
  RobotOutlined,
  CheckOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  useGetNotesQuery,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useEnhanceNoteMutation,
} from '../redux/api/notesApi';
import { useCreateTasksFromSuggestionsMutation } from '../redux/api/internalTasksApi';
import { getErrorMessage } from '../redux/api/baseApi';
import { toast } from 'react-toastify';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const priorityColors = {
  urgent: 'red',
  high: 'orange',
  normal: 'blue',
};

const SuggestedTasks = ({ tasks, onCreateTasks, isCreating, tasksCreated }) => {
  if (!tasks || tasks.length === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <Text strong style={{ fontSize: 12 }}>
          <UnorderedListOutlined style={{ marginRight: 4 }} />
          Suggested Tasks ({tasks.length})
        </Text>
        {!tasksCreated && onCreateTasks && (
          <Button
            size="small"
            type="primary"
            icon={<CheckOutlined />}
            onClick={onCreateTasks}
            loading={isCreating}
          >
            Create All Tasks
          </Button>
        )}
        {tasksCreated && (
          <Tag color="green">Tasks Created</Tag>
        )}
      </div>
      <div style={{ marginTop: 4 }}>
        {tasks.map((task, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '4px 0',
            }}
          >
            <Tag color={priorityColors[task.priority] || 'blue'} style={{ margin: 0 }}>
              {task.priority}
            </Tag>
            <Text style={{ fontSize: 13 }}>{task.title}</Text>
            {task.due_hint && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                — {task.due_hint}
              </Text>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const EnhancePreview = ({ data, onApply, onDismiss, isApplying, onCreateTasks, isCreatingTasks, tasksCreated }) => {
  return (
    <Card
      size="small"
      style={{
        marginTop: 8,
        borderColor: '#1677ff',
        backgroundColor: '#f0f5ff',
      }}
      title={
        <Space size={4}>
          <RobotOutlined />
          <Text strong style={{ fontSize: 13 }}>AI Enhanced Version</Text>
        </Space>
      }
      extra={
        <Space size={4}>
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={onApply}
            loading={isApplying}
          >
            Apply Text
          </Button>
          <Button size="small" onClick={onDismiss}>
            Dismiss
          </Button>
        </Space>
      }
    >
      <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap', fontSize: 13 }}>
        {data.cleaned_text}
      </Paragraph>
      <SuggestedTasks
        tasks={data.suggested_tasks}
        onCreateTasks={onCreateTasks}
        isCreating={isCreatingTasks}
        tasksCreated={tasksCreated}
      />
    </Card>
  );
};

const NotesList = ({ entityType, entityId }) => {
  const { data: notes, isLoading, error } = useGetNotesQuery(
    { entityType, entityId },
    { skip: !entityType || !entityId }
  );
  const [updateNote] = useUpdateNoteMutation();
  const [deleteNote] = useDeleteNoteMutation();
  const [enhanceNote] = useEnhanceNoteMutation();
  const [createTasksFromSuggestions] = useCreateTasksFromSuggestionsMutation();
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [enhancingId, setEnhancingId] = useState(null);
  const [enhanceResults, setEnhanceResults] = useState({});
  const [creatingTasksForNote, setCreatingTasksForNote] = useState(null);
  const [tasksCreatedForNotes, setTasksCreatedForNotes] = useState({});

  const startEditing = (note) => {
    setEditingId(note.id);
    setEditContent(note.content);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditContent('');
  };

  const handleSave = async (id) => {
    if (!editContent.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }
    try {
      await updateNote({ id, content: editContent.trim() }).unwrap();
      toast.success('Note updated');
      setEditingId(null);
      setEditContent('');
    } catch (err) {
      toast.error('Failed to update note: ' + getErrorMessage(err));
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNote(id).unwrap();
      toast.success('Note deleted');
      setEnhanceResults((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (err) {
      toast.error('Failed to delete note: ' + getErrorMessage(err));
    }
  };

  const handleEnhance = async (noteId) => {
    setEnhancingId(noteId);
    try {
      const result = await enhanceNote(noteId).unwrap();
      setEnhanceResults((prev) => ({ ...prev, [noteId]: result }));
    } catch (err) {
      toast.error('AI enhancement failed: ' + getErrorMessage(err));
    } finally {
      setEnhancingId(null);
    }
  };

  const handleApplyEnhancement = async (noteId) => {
    const result = enhanceResults[noteId];
    if (!result) return;

    try {
      await updateNote({ id: noteId, content: result.cleaned_text }).unwrap();
      toast.success('Enhanced note applied');
      setEnhanceResults((prev) => {
        const next = { ...prev };
        delete next[noteId];
        return next;
      });
    } catch (err) {
      toast.error('Failed to apply enhancement: ' + getErrorMessage(err));
    }
  };

  const handleDismissEnhancement = (noteId) => {
    setEnhanceResults((prev) => {
      const next = { ...prev };
      delete next[noteId];
      return next;
    });
  };

  const handleCreateTasks = async (noteId) => {
    const result = enhanceResults[noteId];
    if (!result || !result.suggested_tasks || result.suggested_tasks.length === 0) return;

    setCreatingTasksForNote(noteId);
    try {
      const response = await createTasksFromSuggestions({
        suggestions: result.suggested_tasks,
        noteId: noteId,
        entityType: entityType,
        entityId: entityId,
      }).unwrap();
      toast.success(`${response.created} task(s) created`);
      setTasksCreatedForNotes((prev) => ({ ...prev, [noteId]: true }));
    } catch (err) {
      toast.error('Failed to create tasks: ' + getErrorMessage(err));
    } finally {
      setCreatingTasksForNote(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return <Spin size="small" />;
  }

  if (error) {
    return <Text type="danger">Failed to load notes</Text>;
  }

  if (!notes || notes.length === 0) {
    return <Empty description="No notes yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  return (
    <List
      dataSource={notes}
      renderItem={(note) => {
        const isEnhancing = enhancingId === note.id;
        const enhanceResult = enhanceResults[note.id];

        return (
          <List.Item
            key={note.id}
            actions={
              editingId === note.id
                ? [
                    <Button
                      key="save"
                      type="link"
                      icon={<SaveOutlined />}
                      onClick={() => handleSave(note.id)}
                    >
                      Save
                    </Button>,
                    <Button
                      key="cancel"
                      type="link"
                      icon={<CloseOutlined />}
                      onClick={cancelEditing}
                    >
                      Cancel
                    </Button>,
                  ]
                : [
                    <Button
                      key="enhance"
                      type="link"
                      icon={isEnhancing ? <Spin size="small" /> : <RobotOutlined />}
                      onClick={() => handleEnhance(note.id)}
                      disabled={isEnhancing || !!enhanceResult}
                      size="small"
                      title="Enhance with AI"
                    />,
                    <Button
                      key="edit"
                      type="link"
                      icon={<EditOutlined />}
                      onClick={() => startEditing(note)}
                      size="small"
                    />,
                    <Popconfirm
                      key="delete"
                      title="Delete this note?"
                      onConfirm={() => handleDelete(note.id)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        size="small"
                      />
                    </Popconfirm>,
                  ]
            }
          >
            <div style={{ width: '100%' }}>
              <List.Item.Meta
                description={
                  <Space size={4}>
                    <UserOutlined style={{ fontSize: 11 }} />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {note.first_name} {note.last_name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      &middot; {formatDate(note.created_at)}
                    </Text>
                    {note.updated_at !== note.created_at && (
                      <Text type="secondary" style={{ fontSize: 11, fontStyle: 'italic' }}>
                        (edited)
                      </Text>
                    )}
                  </Space>
                }
                title={
                  editingId === note.id ? (
                    <TextArea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      autoSize={{ minRows: 2, maxRows: 6 }}
                      autoFocus
                    />
                  ) : (
                    <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                      {note.content}
                    </Paragraph>
                  )
                }
              />
              {enhanceResult && (
                <EnhancePreview
                  data={enhanceResult}
                  onApply={() => handleApplyEnhancement(note.id)}
                  onDismiss={() => handleDismissEnhancement(note.id)}
                  isApplying={false}
                  onCreateTasks={
                    enhanceResult.suggested_tasks?.length > 0
                      ? () => handleCreateTasks(note.id)
                      : undefined
                  }
                  isCreatingTasks={creatingTasksForNote === note.id}
                  tasksCreated={!!tasksCreatedForNotes[note.id]}
                />
              )}
            </div>
          </List.Item>
        );
      }}
    />
  );
};

export default NotesList;
